// Demo helpers — write a fully-formed test tournament marked with
// info.isDemo so it's filtered out of the real tournaments list. All demos
// live under /tournaments/{id} and reuse every existing route + listener;
// the only difference is the isDemo flag and a 30-minute "freshness" window
// the admin uses for the cleanup sweep.
import { ref, push, set, update, remove, get, child } from 'firebase/database'
import { db, tref } from './firebase.js'
import CLUB from '../config/club.js'
import { ensureTournamentInfo } from './data.js'
import { buildHandicapEntry } from './handicapEntry.js'

const PARS = (CLUB.course?.pars && CLUB.course.pars.length === 18)
  ? CLUB.course.pars
  : [4, 4, 3, 5, 4, 4, 3, 4, 5, 4, 4, 3, 5, 4, 4, 3, 4, 5]

export const DEMO_TTL_MS = 30 * 60 * 1000

// 18 four-man scramble teams. Southern / Arkansas-flavored names. Each team
// gets exactly one shotgun starting hole 1..18. NO qualifyingScore (scrambles
// don't have qualifying), NO opponentId (scrambles aren't paired). Scores
// are pre-generated so the demo leaderboard looks like a real round in
// progress: 5 FINAL teams at -18..-12, 13 in-progress teams at THRU 2..16.
const SCRAMBLE_DEMO_ROWS = [
  // FINAL — top of the board
  { last: ['Hayes', 'Cooper', 'Mitchell', 'Walker'],     first: ['Bo', 'Cole', 'Wade', 'Hunter'],   targetVsPar: -18, thru: 18 },
  { last: ['Bryant', 'Ross', 'Carter', 'Pierce'],         first: ['Cash', 'Beau', 'Tucker', 'Trey'], targetVsPar: -16, thru: 18 },
  { last: ['Spencer', 'Henderson', 'Patterson', 'Knox'],  first: ['Wyatt', 'Cade', 'Brooks', 'Will'], targetVsPar: -16, thru: 18 },
  { last: ['Mason', 'Daniels', 'Stuart', 'Beasley'],      first: ['Levi', 'Caleb', 'Brett', 'Dustin'], targetVsPar: -14, thru: 18 },
  { last: ['Riley', 'Stevenson', 'Ward', 'Holloway'],     first: ['Travis', 'Logan', 'Jaxon', 'Hayes'], targetVsPar: -12, thru: 18 },
  // In progress
  { last: ['Tate', 'Rhodes', 'Coffey', 'Dawson'],         first: ['Bobby', 'Frank', 'Walter', 'Henry'], targetVsPar: -15, thru: 16 },
  { last: ['Whitaker', 'Greer', 'Latham', 'Sutton'],      first: ['Ben', 'Charlie', 'Sam', 'Jake'],    targetVsPar: -13, thru: 16 },
  { last: ['Vaughn', 'Pickens', 'Faulkner', 'Burgess'],   first: ['Tyler', 'Cody', 'Mason', 'Garrett'], targetVsPar: -12, thru: 14 },
  { last: ['Holcomb', 'Cantrell', 'Easley', 'Pruitt'],    first: ['Reed', 'Drew', 'Owen', 'Luke'],     targetVsPar: -10, thru: 14 },
  { last: ['Slater', 'Boyette', 'Crenshaw', 'McCoy'],     first: ['Jett', 'Asa', 'Boone', 'Jase'],    targetVsPar: -10, thru: 12 },
  { last: ['Murphy', 'Kelley', 'Burns', 'Hatcher'],       first: ['Chase', 'Connor', 'Brody', 'Knox'], targetVsPar: -8,  thru: 12 },
  { last: ['Quinn', 'Avery', 'Hollis', 'Bowers'],         first: ['Eli', 'Zane', 'Lane', 'Crew'],     targetVsPar: -7,  thru: 10 },
  { last: ['Nash', 'Travers', 'Whitlow', 'Sanders'],      first: ['Brent', 'Heath', 'Cliff', 'Russ'],  targetVsPar: -5,  thru: 8 },
  { last: ['Harlan', 'Boggs', 'Mayfield', 'Lott'],        first: ['Jim', 'Tom', 'Ray', 'Joe'],         targetVsPar: -4,  thru: 8 },
  { last: ['Trent', 'Foley', 'Marsh', 'Calhoun'],         first: ['Hayden', 'Cale', 'Rhett', 'Sawyer'], targetVsPar: -3,  thru: 6 },
  { last: ['Bridger', 'Hooks', 'Lyle', 'Ainsworth'],      first: ['Davis', 'Ford', 'Crew', 'Brooks'],   targetVsPar: -2,  thru: 4 },
  { last: ['Pollard', 'Renfro', 'Wadell', 'Tipton'],      first: ['Curt', 'Earl', 'Roy', 'Glen'],       targetVsPar: -1,  thru: 2 },
  { last: ['Tilley', 'Boudreaux', 'Crockett', 'Slaton'],  first: ['Mac', 'Dale', 'Tex', 'Hank'],        targetVsPar: 0,   thru: 2 },
]

// Spread N strokes (negative = under par) across `holes` worth of pars.
// Birdies first, eagles on par-5s when target is steep. Returns stroke
// counts indexed 0..holes-1 (in the team's play order, not hole order).
function makeScrambleRound(targetVsPar, holes, pars) {
  const scores = pars.slice(0, holes)
  let need = targetVsPar
  const indices = [...Array(holes).keys()]
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[indices[i], indices[j]] = [indices[j], indices[i]]
  }
  // Eagles on par-5s when target is deep enough to need them.
  const eagleHoles = []
  if (need < -10) {
    for (const idx of indices) {
      if (need >= -10) break
      if (scores[idx] === 5) {
        scores[idx] -= 2; need += 2; eagleHoles.push(idx)
      }
    }
  }
  // Birdies for the rest.
  for (const idx of indices) {
    if (need === 0) break
    if (eagleHoles.includes(idx)) continue
    if (need < 0 && scores[idx] > 1) { scores[idx] -= 1; need += 1 }
    else if (need > 0) { scores[idx] += 1; need -= 1 }
  }
  return scores
}

// Convert a stroke array into the {hole: {bestBall, updatedAt}} shape the
// app reads, starting from the team's shotgun hole and wrapping around.
function holesFromStart(startHole, strokes) {
  const out = {}
  const baseTs = Date.now() - 90 * 60 * 1000
  for (let i = 0; i < strokes.length; i++) {
    const hole = ((startHole - 1 + i) % 18) + 1
    out[hole] = { bestBall: strokes[i], updatedAt: baseTs + i * 7 * 60 * 1000 }
  }
  return out
}

// Each team: shotgun hole = team index + 1; players are full first/last
// names; team display name is last names joined by '/'. Teams start with NO
// scores so each demo scorecard opens CLEAN on the team's shotgun starting
// hole, ready to score (nothing entered, Save & Next works normally).
function buildScrambleDemoTeams() {
  const teams = {}
  SCRAMBLE_DEMO_ROWS.forEach((row, i) => {
    const startHole = i + 1
    teams[`team-${String(i + 1).padStart(2, '0')}`] = {
      info: {
        name: row.last.join('/'),
        players: row.last.map((ln, idx) => `${row.first[idx]} ${ln}`),
        startingHole: startHole,
      },
      scores: {},   // empty → card opens on startingHole, fresh
    }
  })
  return teams
}

// Pairings builder for the match-play demo. Returns 2 × pairCount teams with
// opponentId crossed so the leaderboard / TV view treat them as matches.
// `nameOffset` lets multiple flights pull non-overlapping name pairs.
function buildMatchDemoTeams(prefix, pairCount = 4, nameOffset = 0) {
  const NAMES = [
    ['Smith', 'Jones'], ['Wilson', 'Davis'], ['Taylor', 'Brown'],
    ['Miller', 'Anderson'], ['Thompson', 'Garcia'], ['Martinez', 'Robinson'],
    ['Clark', 'Lewis'], ['Walker', 'Hall'], ['Lopez', 'Hill'],
    ['Scott', 'Green'], ['Adams', 'Baker'], ['Nelson', 'Carter'],
    ['Mitchell', 'Perez'], ['Roberts', 'Turner'], ['Phillips', 'Campbell'],
    ['Evans', 'Parker'],
  ]
  const teams = {}
  for (let i = 0; i < pairCount; i++) {
    const aIdx = (nameOffset + i * 2)     % NAMES.length
    const bIdx = (nameOffset + i * 2 + 1) % NAMES.length
    const a = NAMES[aIdx]
    const b = NAMES[bIdx]
    const aId = `${prefix}-${i}-a`
    const bId = `${prefix}-${i}-b`
    teams[aId] = {
      info: {
        name: `${a[0]}/${a[1]}`,
        players: a,
        handicap: 8 + i,
        opponentId: bId,
        startingHole: 1,
      },
    }
    teams[bId] = {
      info: {
        name: `${b[0]}/${b[1]}`,
        players: b,
        handicap: 9 + i,
        opponentId: aId,
        startingHole: 1,
      },
    }
  }
  return teams
}

// ── GHIN / Handicap demo ────────────────────────────────────────────────────
// A 4-Ball stroke demo whose players carry realistic handicap INDEXES, a MIX of
// source flags (some GHIN-verified with verifiedAt, some self-reported), and
// per-player playing handicaps on each team — so the whole handicap system
// (allow/disallow self-reported gate, net leaderboard, "who self-reported"
// review) can be tested without hand-building a tournament or calling the live
// GHIN API. Everything is pre-stamped in the demo data (fake GHIN numbers).
const GHIN_DEMO_COURSE = { slopeRating: 128, courseRating: 70.5, coursePar: 71, allowance: 100 }

// [ name, handicapIndex, source, ghin ] — 50/50 ghin vs self mix.
const GHIN_DEMO_TEAMS = [
  // ── Championship Flight ──
  { fid: 'flightA', fname: 'Championship Flight', team: 'Ratliff/Keese',
    p: [['Mark Ratliff', 8.2, 'ghin', '2101234'], ['Cole Keese', 12.5, 'self', null]],
    scores: [[4, 5], [3, 4], [4, 4], [4, 5], [3, 3], [5, 4]] },
  { fid: 'flightA', fname: 'Championship Flight', team: 'Wilson/Brown',
    p: [['Jeff Wilson', 4.1, 'ghin', '2105678'], ['Dan Brown', 15.3, 'self', null]],
    scores: [[4, 6], [3, 4], [5, 4], [4, 4], [4, 3], [4, 5]] },
  { fid: 'flightA', fname: 'Championship Flight', team: 'Taylor/Miller',
    p: [['Sam Taylor', 10.7, 'ghin', '2109012'], ['Rob Miller', 6.9, 'ghin', '2103344']],
    scores: [[5, 4], [4, 3], [4, 4], [5, 4], [3, 4], [4, 4]] },
  { fid: 'flightA', fname: 'Championship Flight', team: 'Davis/Jones',
    p: [['Tom Davis', 18.0, 'self', null], ['Bill Jones', 21.4, 'self', null]] },
  // ── A Flight ──
  { fid: 'flightB', fname: 'A Flight', team: 'Anderson/Thomas',
    p: [['Kyle Anderson', 3.2, 'ghin', '2107788'], ['Nate Thomas', 9.8, 'self', null]],
    scores: [[4, 5], [3, 3], [4, 5], [4, 4], [4, 3], [4, 4]] },
  { fid: 'flightB', fname: 'A Flight', team: 'Jackson/Harris',
    p: [['Eric Jackson', 14.0, 'self', null], ['Paul Harris', 11.2, 'ghin', '2102255']] },
  { fid: 'flightB', fname: 'A Flight', team: 'Martin/Thompson',
    p: [['Greg Martin', 7.5, 'ghin', '2106611'], ['Luke Thompson', 16.7, 'self', null]],
    scores: [[5, 5], [4, 4], [4, 4], [5, 5], [4, 4], [5, 4]] },
  { fid: 'flightB', fname: 'A Flight', team: 'Moore/White',
    p: [['Chris Moore', 22.9, 'self', null], ['Adam White', 5.4, 'ghin', '2108899']] },
]

function ghinDemoEntries(players, verifiedTs) {
  return players.map(([, index, source, ghin]) => buildHandicapEntry({
    ghin, index, source, ...GHIN_DEMO_COURSE,
    now: source === 'ghin' ? verifiedTs : undefined,
  }))
}

// Registrations — one per team, with per-player playerHandicaps carrying the
// source flag + verifiedAt (verified 3 days "before" the event for the GHIN
// ones). This is what the net snapshot + "who self-reported" review read.
function buildGhinDemoRegistrations() {
  const regs = {}
  const baseTs = Date.now()
  const verifiedTs = baseTs - 3 * 24 * 60 * 60 * 1000
  GHIN_DEMO_TEAMS.forEach((t, i) => {
    regs[`reg_${String(i + 1).padStart(2, '0')}`] = {
      teamName: t.team,
      players: t.p.map((pl) => pl[0]),
      playerHandicaps: ghinDemoEntries(t.p, verifiedTs),
      registeredAt: baseTs + i,
      qualifyingStatus: 'complete',
    }
  })
  return regs
}

// Flights + teams — per-player playingHandicaps on each team (net-ready) plus
// partial gross scores (p1/p2/bestBall on holes 1–6) for a few teams so the
// gross-vs-net effect is visible on the leaderboard.
function buildGhinDemoFlights() {
  const flights = {}
  GHIN_DEMO_TEAMS.forEach((t, i) => {
    if (!flights[t.fid]) flights[t.fid] = { info: { name: t.fname, format: '4-Ball Best Ball' }, teams: {} }
    const entries = ghinDemoEntries(t.p)
    const playingHandicaps = entries.map((e) => e.playingHandicap)
    const teamHcp = Math.round(playingHandicaps.reduce((s, x) => s + (x || 0), 0) / (playingHandicaps.length || 1))
    const team = {
      info: {
        name: t.team,
        players: t.p.map((pl) => pl[0]),
        handicap: teamHcp,
        playingHandicaps,
        startingHole: (i % 18) + 1,
      },
    }
    if (Array.isArray(t.scores)) {
      const scores = {}
      t.scores.forEach(([p1, p2], hi) => {
        scores[hi + 1] = { p1score: p1, p2score: p2, bestBall: Math.min(p1, p2), updatedAt: Date.now() }
      })
      team.scores = scores
    }
    flights[t.fid].teams[`team_${String(i + 1).padStart(2, '0')}`] = team
  })
  return flights
}

// Split a flat teams map into N flights (for the "vs FLIGHT" demos). The
// scorecard ranks within a team's own flight (ScoreApp reads flight.teams), so
// flighting the field here is exactly what makes the leaderboard show FLIGHT
// standings instead of the whole field. A single-flight def (teams()) = vs FIELD.
// Split a flat teams map into flights of 8 (per TOURNAMENT_FORMATS.md §4.1).
// flighted-no-qual just splits into 8s in team order → last flight may be partial
// (36 → 8,8,8,8,4 = 5 flights; 18 → 8,8,2 = 3 flights). The scorecard ranks within
// a team's flight, so this is what makes "vs FLIGHT" happen.
const FLIGHT_NAMES = ['Championship Flight', 'Flight 1', 'Flight 2', 'Flight 3', 'Flight 4', 'Flight 5', 'Flight 6']
function flightsOf8(teamsObj, format) {
  const entries = Object.entries(teamsObj)
  const out = {}
  let fi = 0
  for (let i = 0; i < entries.length; i += 8) {
    const slice = entries.slice(i, i + 8)
    out[`flight-${fi + 1}`] = { info: { name: FLIGHT_NAMES[fi] || `Flight ${fi}`, format }, teams: Object.fromEntries(slice) }
    fi++
  }
  return out
}

// 36 two-man Best Ball teams (72 players) — re-paired from the 18×4 scramble
// roster. Team score = one number per hole (best ball). Field spread for a live
// leaderboard. Shotgun holes are assigned at build time (assignShotgunHolesByMatch,
// 2 teams per hole) so tee times come pre-populated.
function buildBestBallDemoTeams() {
  const players = []
  SCRAMBLE_DEMO_ROWS.forEach((row) => row.last.forEach((ln, i) => players.push({ first: row.first[i], last: ln })))
  const teams = {}
  for (let i = 0; i < 36; i++) {
    const p = [players[i * 2], players[i * 2 + 1]]
    const isFinal = i < 8
    const targetVsPar = isFinal ? (-4 + i) : ((i % 12) - 2) // finals -4..+3; rest -2..+9
    const thru = isFinal ? 18 : 6 + ((i * 5) % 11)          // in-progress 6..16
    const strokes = makeScrambleRound(targetVsPar, thru, PARS)
    teams[`team-${String(i + 1).padStart(2, '0')}`] = {
      info: {
        name: p.map((x) => x.last).join('/'),
        players: p.map((x) => `${x.first} ${x.last}`),
      },
      scores: holesFromStart(1, strokes),
    }
  }
  return teams
}

// Quota teams — 36 two-man teams with a per-team quota (36 − hcp per player,
// summed). Spread the quota so the demo board shows some over, some under.
function buildQuotaDemoTeams() {
  const teams = buildBestBallDemoTeams()
  Object.values(teams).forEach((t, i) => { t.info.quota = 32 + (i % 9) }) // 32..40
  return teams
}

const DEMO_FORMATS = {
  fourBallMatch: {
    label: '4-Ball Match Play',
    info: {
      name: '4-Ball Match Play Demo',
      format: '4-Ball Best Ball',
      playStyle: 'match',
      startType: 'shotgun',
      teeStyle: 'Shotgun start',
    },
    // 3 flights × 4 pairings = 12 pairings total, enough content to force
    // overflow on /tv and /tv/scores so the auto-scroll loop has something
    // to scroll past. Each pairing has opponentId set both ways so the
    // match-status logic kicks in immediately.
    flights: () => ({
      championship: {
        info: { name: 'Championship Flight', format: '4-Ball Best Ball' },
        teams: buildMatchDemoTeams('champ', 4, 0),
      },
      'flight-a': {
        info: { name: 'A Flight', format: '4-Ball Best Ball' },
        teams: buildMatchDemoTeams('a', 4, 4),
      },
      'flight-b': {
        info: { name: 'B Flight', format: '4-Ball Best Ball' },
        teams: buildMatchDemoTeams('b', 4, 8),
      },
    }),
  },
  scramble: {
    label: 'Scramble',
    info: {
      name: 'Spring Scramble Demo',
      tournamentDays: 1,
      format: 'Scramble',
      playStyle: 'stroke',
      startType: 'shotgun',
      teeStyle: 'Shotgun start',
      scrambleTeamSize: 4,
      scramblePlayersPerHole: 1,
      scrambleStyle: 'standard',
      scrambleHandicapsEnabled: false,
      scrambleMulligans: 0,
      scrambleMinDriveEnabled: false,
      scrambleMinDrivesPerPlayer: 0,
    },
    flightName: 'Spring Scramble Flight',
    teams: () => buildScrambleDemoTeams(),
    // Side contests pre-seeded so the leaderboard's contest strip has
    // something to show. Winners blank — admins fill in if/when awarded.
    contests: () => ({
      kps: [
        { hole: 3,  winner: '', distance: '' },
        { hole: 7,  winner: '', distance: '' },
        { hole: 12, winner: '', distance: '' },
        { hole: 16, winner: '', distance: '' },
      ],
      longdrive: { hole: 8, winner: '', distance: '' },
      hio: { hole: 15, prize: '2025 Club Membership', winner: '' },
    }),
    // Tee Times tab reads tournaments/{id}/teeTimes/1/waves/wave1/startTime
    // for shotgun start. Pre-set 8:00 AM so the demo shows it out of the box.
    teeTimes: () => ({
      1: { waves: { wave1: { startTime: '8:00 AM' } } },
    }),
  },
  // ── Scramble · FLIGHTED (vs FLIGHT) — same teams, split into flights so the
  //    scorecard's field ranking stays WITHIN each flight. ──
  //    18 four-man teams split into flights of 8 (8,8,2). ──
  scrambleFlight: {
    label: 'Scramble · Flighted',
    info: {
      name: 'Flighted Scramble Demo',
      tournamentDays: 1,
      format: 'Scramble',
      playStyle: 'stroke',
      startType: 'shotgun',
      teeStyle: 'Shotgun start',
      scrambleTeamSize: 4,
      scramblePlayersPerHole: 1,
      scrambleStyle: 'standard',
      scrambleHandicapsEnabled: false,
    },
    flights: () => flightsOf8(buildScrambleDemoTeams(), 'Scramble'),
    teeTimes: () => ({ 1: { waves: { wave1: { startTime: '8:00 AM' } } } }),
  },
  // ── Best Ball (Four-Ball) · vs FIELD — 36 two-man teams (72 players), one
  //    flight. Shotgun holes + tee times assigned at build time (2 teams/hole). ──
  bestBallField: {
    label: 'Best Ball · vs Field',
    info: {
      name: 'Best Ball Field Demo',
      tournamentDays: 1,
      format: '4-Ball Best Ball',
      playStyle: 'stroke',
      startType: 'shotgun',
      teeStyle: 'Shotgun start',
    },
    flightName: 'Best Ball — Full Field',
    teams: () => buildBestBallDemoTeams(),
  },
  // ── Best Ball · FLIGHTED — 36 two-man teams split into flights of 8
  //    (8,8,8,8,4 = 5 flights). ──
  bestBallFlight: {
    label: 'Best Ball · Flighted',
    info: {
      name: 'Flighted Best Ball Demo',
      tournamentDays: 3,
      bracketDays: 3,
      format: '4-Ball Best Ball',
      playStyle: 'stroke',
      startType: 'shotgun',
      teeStyle: 'Shotgun start',
    },
    flights: () => flightsOf8(buildBestBallDemoTeams(), '4-Ball Best Ball'),
  },
  // ── Shamble · vs FIELD — 2-man, best drive then own ball; one team score/hole. ──
  shamble: {
    label: 'Shamble · vs Field',
    info: { name: 'Shamble Demo', tournamentDays: 1, format: 'Shamble', playStyle: 'stroke', startType: 'shotgun', teeStyle: 'Shotgun start' },
    flightName: 'Shamble — Full Field',
    teams: () => buildBestBallDemoTeams(),
  },
  // ── Alternate Shot / Foursomes · vs FIELD — 2-man, one ball, one score/hole. ──
  alternateShot: {
    label: 'Alternate Shot · vs Field',
    info: { name: 'Alternate Shot Demo', tournamentDays: 1, format: 'Alternate Shot', playStyle: 'stroke', startType: 'shotgun', teeStyle: 'Shotgun start' },
    flightName: 'Alternate Shot — Full Field',
    teams: () => buildBestBallDemoTeams(),
  },
  // ── Stableford · vs FIELD — points/hole (par 2, birdie 3, eagle 4); high wins. ──
  stableford: {
    label: 'Stableford · vs Field',
    info: { name: 'Stableford Demo', tournamentDays: 1, format: 'Stableford', playStyle: 'stroke', startType: 'shotgun', teeStyle: 'Shotgun start' },
    flightName: 'Stableford — Full Field',
    teams: () => buildBestBallDemoTeams(),
  },
  // ── Quota · vs FIELD — points (bogey 1/par 2/birdie 4/eagle 8) vs 36−hcp quota. ──
  quota: {
    label: 'Quota · vs Field',
    info: { name: 'Quota Demo', tournamentDays: 1, format: 'Quota', playStyle: 'stroke', startType: 'shotgun', teeStyle: 'Shotgun start' },
    flightName: 'Quota — Full Field',
    teams: () => buildQuotaDemoTeams(),
  },
  // ── Skins · vs FIELD — low score/hole wins, ties carry & stack (carryover ON). ──
  skins: {
    label: 'Skins · vs Field',
    info: { name: 'Skins Demo', tournamentDays: 1, format: 'Skins', playStyle: 'stroke', startType: 'shotgun', teeStyle: 'Shotgun start' },
    flightName: 'Skins — Full Field',
    teams: () => buildBestBallDemoTeams(),
  },
  individual: {
    label: 'Stroke Play Individual',
    info: {
      name: 'Stroke Play Individual Demo',
      format: 'Stroke Play Individual',
      playStyle: 'stroke',
    },
    flightName: 'Demo Flight',
    teams: () => ({
      player1: {
        info: { name: 'Mark Ratliff', players: ['Mark Ratliff'], handicap: 8, startingHole: 1 },
      },
      player2: {
        info: { name: 'Cole Keese', players: ['Cole Keese'], handicap: 9, startingHole: 1 },
      },
    }),
  },
  ghinHandicap: {
    kind: 'ghinHandicap',
    label: 'GHIN / Handicap Demo',
    info: {
      name: 'GHIN / Handicap Demo',
      format: '4-Ball Best Ball',
      playStyle: 'stroke',
      startType: 'shotgun',
      teeStyle: 'Shotgun start',
      handicapMode: 'optional',
      allowSelfReported: true,
      slopeRating: GHIN_DEMO_COURSE.slopeRating,
      courseRating: GHIN_DEMO_COURSE.courseRating,
      coursePar: GHIN_DEMO_COURSE.coursePar,
      handicapAllowance: GHIN_DEMO_COURSE.allowance,
      teeName: 'Blue',
    },
    flights: () => buildGhinDemoFlights(),
    registrations: () => buildGhinDemoRegistrations(),
  },
}

// Pre-seeded teams for the full-tournament demo. TWO selectable rosters:
//   96-team → 12 flights of 8   |   104-team → 13 flights of 8
// Scores 68–86 are bell-curve-ish so Auto-Flight by Qualifying Score produces a
// believable spread (Championship Flight gets the low 68s/69s, last flight the 85s+).
const FULL_TOURNAMENT_TEAMS_96 = [
  // 68 (Championship territory) — 1 team
  ['Ratliff/Keese', 68],
  // 69 — 3 teams
  ['Wilson/Brown', 69], ['Taylor/Miller', 69], ['Davis/Jones', 69],
  // 70 — 5 teams
  ['Moore/White', 70], ['Anderson/Thomas', 70], ['Jackson/Harris', 70],
  ['Martin/Thompson', 70], ['Garcia/Martinez', 70],
  // 71 — 5 teams
  ['Robinson/Clark', 71], ['Rodriguez/Lewis', 71], ['Lee/Walker', 71],
  ['Hall/Allen', 71], ['Young/Hernandez', 71],
  // 72 — 5 teams
  ['King/Wright', 72], ['Lopez/Hill', 72], ['Scott/Green', 72],
  ['Adams/Baker', 72], ['Nelson/Carter', 72],
  // 73 — 6 teams
  ['Mitchell/Perez', 73], ['Roberts/Turner', 73], ['Phillips/Campbell', 73],
  ['Evans/Parker', 73], ['Edwards/Collins', 73], ['Stewart/Sanchez', 73],
  // 74 — 7 teams
  ['Morris/Rogers', 74], ['Reed/Cook', 74], ['Morgan/Bell', 74],
  ['Cooper/Murphy', 74], ['Richardson/Bailey', 74], ['Cox/Rivera', 74],
  ['Howard/Gonzalez', 74],
  // 75 — 7 teams
  ['Ward/Bryant', 75], ['Torres/Alexander', 75], ['Peterson/Russell', 75],
  ['Gray/Griffin', 75], ['Hayes/Diaz', 75], ['James/Jordan', 75],
  ['Watson/Perry', 75],
  // 76 — 8 teams
  ['Brooks/Powell', 76], ['Kelly/Long', 76], ['Sanders/Patterson', 76],
  ['Price/Hughes', 76], ['Bennett/Flores', 76], ['Wood/Washington', 76],
  ['Barnes/Cole', 76], ['Ross/Henderson', 76],
  // 77 — 8 teams
  ['Jenkins/North', 77], ['Perry/Ford', 77], ['Hamilton/Graham', 77],
  ['Sullivan/Wallace', 77], ['Butler/Simmons', 77], ['Foster/Coleman', 77],
  ['Warren/Spencer', 77], ['Dixon/Hawkins', 77],
  // 78 — 7 teams
  ['Perkins/Burns', 78], ['Reyes/Stone', 78], ['Boyd/Hunter', 78],
  ['Webb/Tucker', 78], ['Fuller/Wells', 78], ['Carroll/Gibson', 78],
  ['Holmes/Black', 78],
  // 79 — 7 teams
  ['Mason/Bishop', 79], ['Pierce/Lawson', 79], ['Owens/Daniels', 79],
  ['Olson/Stevens', 79], ['Lawrence/Pearson', 79], ['Wagner/Hopkins', 79],
  ['Riley/Davidson', 79],
  // 80 — 6 teams
  ['Bowman/Knight', 80], ['Andrews/Webster', 80], ['Walters/Estrada', 80],
  ['Schmidt/Cunningham', 80], ['Lambert/Glover', 80], ['Reeves/Banks', 80],
  // 81 — 5 teams
  ['Bradley/Vargas', 81], ['Hampton/Day', 81], ['Padilla/Cross', 81],
  ['Townsend/Saunders', 81], ['Buchanan/Reilly', 81],
  // 82 — 5 teams
  ['Conner/Dawson', 82], ['Holt/Mills', 82], ['Marsh/Strickland', 82],
  ['Drake/Burgess', 82], ['Yates/Newman', 82],
  // 83 — 4 teams
  ['Schultz/Bowen', 83], ['Garrett/Steele', 83], ['McKenzie/Curtis', 83],
  ['Booth/Massey', 83],
  // 84 — 4 teams
  ['Stein/Pope', 84], ['Norris/Hodge', 84], ['Vasquez/McCarthy', 84],
  ['Greer/Hancock', 84],
  // 85 (back of the pack) — 3 teams
  ['Hines/Mullins', 85], ['Tate/Mack', 85], ['Sherman/Hubbard', 85],
]

// 8 more teams (scores 85–86) that extend the 96 into a full 13th flight → 104.
const FULL_TOURNAMENT_EXTRA_8 = [
  ['Frost/Nash', 85], ['Vaughn/Barr', 85], ['Odom/Kramer', 85], ['Lyons/Whitaker', 86],
  ['Meyer/Bock', 86], ['Franklin/Dorsey', 86], ['Kirby/Ashby', 86], ['Sutton/Beasley', 86],
]
const FULL_TOURNAMENT_TEAMS_104 = [...FULL_TOURNAMENT_TEAMS_96, ...FULL_TOURNAMENT_EXTRA_8]

// ── DEMO FIELD SIZE ──────────────────────────────────────────────────────────
// The Full Tournament Demo roster (96 → 12 flights of 8, 104 → 13 flights of 8)
// is chosen at launch time via the Demo Launcher toggle — see rosterForSize()
// below and the FieldSizeToggle in DemoLauncher.jsx. Default is 96.
function rosterForSize(size) {
  // Slice the sorted roster to the requested team count (8-104). 96 and 104
  // return their full rosters unchanged; anything between is the top N teams so
  // the demo can be shown at any size (1-13 flights of 8).
  const n = Math.max(8, Math.min(104, Math.round(Number(size) || 96)))
  const base = n > 96 ? FULL_TOURNAMENT_TEAMS_104 : FULL_TOURNAMENT_TEAMS_96
  return base.slice(0, n)
}

// Demo registration rows for an N-flight PBCC 4-Ball — 8 teams per flight, so
// 1 flight = 8 teams … 13 flights = 104. Sliced from the top of the sorted
// roster so the qualifying scores stay a believable spread (Championship gets
// the 68s/69s). The 13-flight case uses the 104-team roster; everything else
// slices the 96-team roster. Rows carry a completed qualifying score so the AI
// build treats them as a qualified field.
export function demoRegistrationRows(flights) {
  const n = Math.max(1, Math.min(13, Math.round(Number(flights) || 1)))
  const roster = n >= 13 ? FULL_TOURNAMENT_TEAMS_104 : FULL_TOURNAMENT_TEAMS_96
  const today = new Date().toISOString().slice(0, 10)
  return roster.slice(0, n * 8).map(([teamName, score]) => ({
    teamName,
    players: teamName.split('/'),
    qualifyingScore: score,
    qualifyingStatus: 'complete',
    qualifyingDate: today,
  }))
}

const FULL_TOURNAMENT_DEF = {
  kind: 'full', // DemoLauncher branches on this — no per-team QR codes for the full-tournament demo
  label: 'Full Tournament Demo',
  info: {
    name: '4-Ball Championship — FULL DEMO',
    format: '4-Ball Best Ball',
    playStyle: 'match',
    teeStyle: 'Shotgun start',
    tournamentDays: 3,
    bracketDays: 3,
  },
  teamRows: FULL_TOURNAMENT_TEAMS_96, // default preview; launch toggle can swap to 104
}

// Practice mode — same scoring/listener pipelines as a real tournament,
// but flagged isDemo:true + info.practice:true so the dashboard sorts it
// into the Demos & Practice section AND the tournament shell renders a
// PRACTICE banner. Each mode reuses an existing demo format builder so
// the admin always lands on a tournament with realistic sample data they
// can edit, score against, reset, or delete.
const PRACTICE_FORMATS = {
  practice4Ball:    { sourceKey: 'fourBallMatch', name: 'Practice — 4-Ball Match Play' },
  practiceScramble: { sourceKey: 'scramble',      name: 'Practice — 4-Man Scramble' },
}

export async function createPracticeTournament(practiceKey) {
  const def = PRACTICE_FORMATS[practiceKey]
  if (!def) throw new Error('Unknown practice mode: ' + practiceKey)
  const { tournamentId } = await createDemoTournament(def.sourceKey)
  // Re-stamp the just-created tournament's name + practice flag so the
  // banner / list / sort all see this as a practice tournament, not just
  // a generic demo.
  await update(tref(`tournaments/${tournamentId}/info`), {
    name: def.name,
    practice: true,
  })
  return { tournamentId }
}

export const DEMO_FORMAT_KEYS = Object.keys(DEMO_FORMATS)
export const DEMO_FORMAT_META = DEMO_FORMATS

export async function createDemoTournament(formatKey, opts = {}) {
  if (formatKey === 'fullTournament') return createFullTournamentDemo(opts.size)
  if (formatKey === 'strokePlay') return createStrokePlayDemo()
  const def = DEMO_FORMATS[formatKey]
  if (!def) throw new Error('Unknown demo format: ' + formatKey)
  const id = push(tref('tournaments')).key
  // teeStyle defaults to 'Tee times' but defer to def.info.teeStyle when
  // the format spec sets one (Scramble shotgun, etc). Same for status.
  const tree = {
    info: {
      teeStyle: 'Tee times',
      status: 'active',
      ...def.info,
      course: CLUB.course?.name || 'Demo Course',
      date: new Date().toISOString().slice(0, 10),
      pars: PARS,
      isDemo: true,
      demoCreatedAt: Date.now(),
      createdAt: Date.now(),
    },
    // def.flights builder (multi-flight) takes precedence; otherwise fall
    // back to a single flight with def.flightName + def.teams().
    flights: def.flights
      ? def.flights()
      : {
          flightA: {
            info: { name: def.flightName, format: def.info.format },
            teams: def.teams(),
          },
        },
  }
  // Optional pre-seeded registrations (e.g. the GHIN/Handicap demo) so the
  // per-player source-flagged handicap data exists for the review + net views.
  if (def.registrations) tree.registrations = def.registrations()
  // Optional pre-seeded side contests (KP, LD, HIO, …) for demos that
  // want the leaderboard contest strip to show something out of the box.
  if (def.contests) tree.contests = def.contests()
  // Optional pre-seeded teeTimes shape (e.g. shotgun start time) so the
  // Tee Times tab shows real data immediately on demo open.
  if (def.teeTimes) tree.teeTimes = def.teeTimes()
  ensureTournamentInfo(tree.info)
  await set(tref(`tournaments/${id}`), tree)
  return { tournamentId: id, def }
}

// Full-tournament seed — writes NOTHING to /tournaments. The button is a
// pure UI trigger: it builds the 96/104 registration records in memory so the
// DemoLauncher modal can show the roster preview, and it returns them
// alongside the def so the dashboard can flip demoMode on the AI panel.
// The /templates/demos/pbcc-4-ball template (already in Firebase) is the
// source of truth — the AI's COPY path resolves it whenever the director
// asks to create a 4-Ball tournament.
async function createFullTournamentDemo(size = 96) {
  const teamRows = rosterForSize(size)
  const today = new Date().toISOString().slice(0, 10)
  const baseTs = Date.now()
  const registrations = {}
  teamRows.forEach(([teamName, score], i) => {
    const regId = `reg_${String(i + 1).padStart(2, '0')}`
    registrations[regId] = {
      teamName,
      players: teamName.split('/'),
      qualifyingScore: score,
      qualifyingStatus: 'complete',
      qualifyingDate: today,
      registeredAt: baseTs + i,
    }
  })
  return {
    def: { ...FULL_TOURNAMENT_DEF, teamRows },
    teamRows,
    registrations,
    size: teamRows.length,
  }
}

// 36 individual stroke-play players with realistic USGA handicap indexes
// from 2.4 to 28.0 — decimals like a real handicap roster. Covers low,
// mid, and high brackets so the leaderboard's gross-vs-net spread looks
// like an actual club tournament.
const STROKE_PLAY_PLAYERS = [
  ['Bryce Henderson',  2.4], ['Will Patterson',  3.1], ['Wyatt Chen',       4.2],
  ['Cole Reynolds',    4.8], ['Logan Bishop',    5.5], ['Hunter Bradley',   6.3],
  ['Levi Caldwell',    7.0], ['Drew Saunders',   7.8], ['Jaxon McCoy',      8.4],
  ['Brooks Whitman',   9.1], ['Cade Hollister', 10.0], ['Tate Marlow',     10.7],
  ['Wade Sutherland', 11.5], ['Brett Cavanaugh',12.2], ['Reed Donovan',    12.9],
  ['Asa Crawford',    13.6], ['Boone Kimball',  14.3], ['Sawyer Fennell',  15.0],
  ['Beau Kirkland',   15.8], ['Tucker Ainsley', 16.5], ['Heath Worthing',  17.2],
  ['Ford Hollins',    18.0], ['Mac Tilley',     18.7], ['Clay Boudreaux',  19.4],
  ['Dale Crockett',   20.1], ['Russ Whittaker', 20.8], ['Brent Stockton',  21.5],
  ['Glen Pickering',  22.2], ['Earl Calloway',  22.9], ['Cliff Hargrove',  23.6],
  ['Curt Beaumont',   24.3], ['Roy Faulkner',   25.0], ['Hank Slaton',     25.7],
  ['Tex Pemberton',   26.4], ['Roscoe Driggers',27.1], ['Jim Hatfield',    28.0],
]

const STROKE_PLAY_DEF = {
  kind: 'strokePlay', // DemoLauncher branches on this for the active panel
  label: 'Stroke Play Demo',
  info: {
    name: 'PBCC Stroke Play Demo',
    format: 'Stroke Play Individual',
    playStyle: 'stroke',
    startType: 'shotgun',
    teeStyle: 'Shotgun start',
  },
  playerRows: STROKE_PLAY_PLAYERS,
}

// Stroke-play seed — writes NOTHING to /tournaments. Builds the complete
// tree in memory and saves it to /templates/demos/stroke-play-36 as a
// permanent reference. Returns the def + player roster so the
// DemoLauncher modal can show the preview.
//
// Data model — verified against the codebase, not guessed:
//   - info.format === 'Stroke Play Individual' is the canonical gate string
//     (src/lib/data.js:837 isSolo check, src/score/ScoreApp.jsx:277 isPlayer
//     check). All scoring + leaderboard machinery branches on it.
//   - Each player is stored as a SINGLE-PLAYER team under one flight:
//       flights/{fid}/teams/{teamId}/info = {
//         name, players: [name], handicap, handicapIndex, startingHole
//       }
//     The scoring engine reads team.info.handicap as the integer playing
//     handicap (src/lib/scoring.js:65 teamNet), and team.info.players[0]
//     is the display name (src/score/ScoreApp.jsx:280).
//   - Two players share a tee box by carrying the same startingHole, but
//     they score independently (no shared scorecard, no opponentId — solo
//     rows in buildWaveGroups src/lib/data.js:846).
//
// Group / wave design: 36 players ÷ 2 per group = 18 groups, one group
// per shotgun hole 1-18. Single wave at 8:00 AM fits the entire field.
async function createStrokePlayDemo() {
  const today = new Date().toISOString().slice(0, 10)

  // 36 single-player teams. handicapIndex is the decimal USGA index
  // (display + lookup); handicap is the rounded integer playing handicap
  // that teamNet() subtracts from gross. Same teamId across teams +
  // wave1 assignments so the Tee Times tab joins them correctly.
  const teams = {}
  const players = {}
  STROKE_PLAY_PLAYERS.forEach(([name, handicapIndex], idx) => {
    const teamId = `player-${String(idx + 1).padStart(2, '0')}`
    const startingHole = Math.floor(idx / 2) + 1 // pair (0,1)→1, (2,3)→2, …
    const handicap = Math.round(handicapIndex) // playing handicap (integer)
    teams[teamId] = {
      info: {
        name,
        players: [name],
        handicap,
        handicapIndex,
        startingHole,
      },
    }
    // Optional /players roster — mirrors the standard tournament shape
    // so the Players tab and any handicap-by-name lookups see the field.
    players[`p${String(idx + 1).padStart(2, '0')}`] = {
      name,
      handicapIndex,
    }
  })

  // Single flight — Stroke Play Individual fields are flat lists, not
  // flighted brackets. buildWaveGroups treats every team in it as a solo
  // row regardless of flight count.
  const flights = {
    main: {
      info: { name: 'Stroke Play Field', format: 'Stroke Play Individual' },
      teams,
    },
  }

  // Shotgun wave 1 at 8:00 AM. assignments map is keyed by teamId; value
  // is the starting hole as a string — what TeeTimesTab + the scorecard's
  // resolveStartingHole walker expect. Two players with the same hole
  // share a tee box and score individually.
  const wave1Assignments = {}
  for (const [tid, t] of Object.entries(teams)) {
    wave1Assignments[tid] = String(t.info.startingHole)
  }
  const teeTimes = {
    1: {
      waves: {
        wave1: {
          startTime: '8:00 AM',
          flights: { main: true },
          assignments: wave1Assignments,
        },
      },
    },
  }

  const tree = {
    info: {
      ...STROKE_PLAY_DEF.info,            // name/format/playStyle/startType/teeStyle
      course: CLUB.course?.name || 'Tournament Course',
      date: today,
      pars: PARS,
      handicapMode: 'index',              // signals USGA-index calc to handicap engine
      allowance: 100,                     // 100% by default; admin can adjust
      scoringEnabled: true,
      status: 'open',
      isDemo: true,
      demoCreatedAt: Date.now(),
      createdAt: Date.now(),
    },
    flights,
    players,
    teeTimes,
  }
  ensureTournamentInfo(tree.info)

  // Save the complete tree as a permanent template under
  // /templates/demos/stroke-play-36. Stripped of live data via
  // tournamentToTemplate(); independent of /tournaments lifecycle.
  const { tournamentToTemplate } = await import('./templates.js')
  const templateTree = tournamentToTemplate(tree)
  templateTree.meta = {
    templateName: 'PBCC Stroke Play (36 Players)',
    savedAt: Date.now(),
    savedBy: 'strokePlayDemo',
    sourceTournamentId: null,
  }
  await set(tref('templates/demos/stroke-play-36'), templateTree)

  return {
    def: STROKE_PLAY_DEF,
    playerRows: STROKE_PLAY_PLAYERS,
  }
}

// Sweep all tournaments and remove any demos older than the TTL.
export async function cleanupExpiredDemos() {
  const snap = await get(tref('tournaments'))
  const all = snap.val() || {}
  const now = Date.now()
  let removed = 0
  for (const [id, t] of Object.entries(all)) {
    if (!t?.info?.isDemo) continue
    const age = now - (t.info.demoCreatedAt || 0)
    if (age > DEMO_TTL_MS) {
      await remove(tref(`tournaments/${id}`))
      removed++
    }
  }
  return removed
}

// Force-remove every demo, regardless of age. Used by the "remove all" button.
export async function deleteAllDemos() {
  const snap = await get(tref('tournaments'))
  const all = snap.val() || {}
  let removed = 0
  for (const [id, t] of Object.entries(all)) {
    if (!t?.info?.isDemo) continue
    await remove(tref(`tournaments/${id}`))
    removed++
  }
  return removed
}
